import React, { useEffect, useState } from 'react';
import { Checkbox, Stack, Typography } from '@mui/material';
import { AuditLogEventName } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { IAuditLogItemResponse } from 'models/auditLog';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import FormLabel from 'components/atoms/FormLabel';
import { useDictionary } from 'models/dictionaryContext';
import { formatAdminSystemEventText } from './helpers';

interface IAdministrationSystemSettingsEventDetails {
  auditLog: IAuditLogItemResponse;
}

const AdministrationSystemSettingsEventDetails = ({
  auditLog,
}: IAdministrationSystemSettingsEventDetails) => {
  const [details, setDetails] = useState<any | null>();
  const [previousDetails, setPreviousDetails] = useState<any | null>();
  const { getCurrency, currency, getMeasurement, measurement } = useDictionary();
  const { t } = useTranslations();

  useEffect(() => {
    getCurrency();
    getMeasurement();
  }, []);

  const keysFromDictionary = [
    'currency',
    'measurement_unit',
    'two_fa_offline_enabled',
    'two_fa_enabled',
    'etrace_reports_enabled',
    'iarms_reports_enabled',
  ];

  const getFromDictionary = (key: any, value: string) => {
    switch (key) {
      case 'currency':
        if (currency) {
          const result = currency.filter((item) => value === item.uniqueId);
          return result.length ? result[0].name : '';
        }
        break;
      case 'measurement_unit':
        if (measurement) {
          const result = measurement.filter((item) => value === item.uniqueId);
          return result.length ? result[0].name : '';
        }
        break;
      case 'two_fa_offline_enabled':
      case 'two_fa_enabled':
      case 'etrace_reports_enabled':
      case 'iarms_reports_enabled':
        if (value) {
          return value === 'true' ? t('general.on.text') : t('general.off.text');
        }
        break;
      default:
        break;
    }
    return '';
  };

  const prepareOptions = (options: any) => (
    <Grid size={{ xs: 12 }} container>
      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
        {t('authenticationSetup.tomePeriods.label')}
      </Typography>
      <Grid size={{ xs: 12 }} container>
        {options.map((option: any) => (
          <Grid>
            <Checkbox checked={option.enabled} disabled={true} />
            <FormLabel name={option.daysToExpire} label={option.label} />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );

  useEffect(() => {
    if (auditLog?.previousObjectValue) {
      (async () => {
        const previousObjectValue = auditLog?.previousObjectValue as any;
        const text = keysFromDictionary.includes(previousObjectValue.key)
          ? getFromDictionary(previousObjectValue.key, previousObjectValue.value)
          : previousObjectValue.value;

        const previousData = {
          text: text || '',
          eventName: auditLog.eventName || '',
          type: previousObjectValue.description || '',
          options: previousObjectValue.options || null,
        };
        setPreviousDetails(previousData);
      })();
    }

    if (auditLog?.postObjectValue) {
      (async () => {
        const postObjectValue = auditLog?.postObjectValue as any;

        const text = keysFromDictionary.includes(postObjectValue.key)
          ? getFromDictionary(postObjectValue.key, postObjectValue.value)
          : postObjectValue.value;

        const postData = {
          text: text || '',
          eventName: auditLog.eventName || '',
          type: postObjectValue.description || '',
          options: postObjectValue.options || null,
        };
        setDetails(postData);
      })();
    }
  }, [auditLog, currency, measurement]);

  return (
    <>
      {previousDetails && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {`${t('systemSettings.header')} ${t('auditLogDetails.before.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            pageType={EDetailsPageTypes.auditLogView}
            title={previousDetails.eventName}
            disableActions={true}
          >
            {!previousDetails.options && (

              <Grid size={{ xs: 12 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {previousDetails.type}
                  </Typography>
                  <Typography variant="body2">
                    {formatAdminSystemEventText(previousDetails.text)}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {previousDetails.options && prepareOptions(previousDetails.options)}
          </DetailsTemplate>
        </>
      )}

      {details && (
        <>
          <AuditLogEventName sx={{ marginTop: '20px' }}>
            <Typography variant="h5">
              {`${t('systemSettings.header')} ${t('auditLogDetails.post.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            pageType={EDetailsPageTypes.auditLogView}
            title={details.eventName}
            disableActions={true}
          >
            {!details.options && (

              <Grid size={{ xs: 12 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {details.type}
                  </Typography>
                  <Typography variant="body2">
                    {formatAdminSystemEventText(details.text)}
                  </Typography>
                </Stack>
              </Grid>
            )}
            {details.options && prepareOptions(details.options)}
          </DetailsTemplate>
        </>
      )}
    </>
  );
};

export default AdministrationSystemSettingsEventDetails;
